import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BoundingBoxImage, ImagesDatabase } from "@/components/ImageCrop";
import { ZoomableImage } from "@/components/ZoomableImage";
import { cn } from "@/lib/utils";
import { PhotoPreviewLabels } from "./PhotoPreviewLabels";
import { PhotoRoomLabels } from "./PhotoRoomLabels";
import {
  boxForPrediction,
  descriptionForPrediction,
  formatPhotoNumberOverlayTimestamp,
  getPhotoCapturedAtMs,
  getPhotoImageUrl,
  highestScoringPredictionForFile,
  isEditableKeyboardTarget,
} from "./photos.utils";
import type { PhotoPreviewProps } from "./photos.types";

export function PhotoPreview({
  onClose,
  photos,
  selectedPhotoId,
  predictions,
  includePredictionsInCaption,
  estimateId,
  onAddLabel,
  onRemoveLabel,
  variant = "sidebar",
}: PhotoPreviewProps) {
  // Tracked by id, not index: labelling replaces the photos array, and an index held in state
  // would be re-derived from `selectedPhotoId` on every such change, snapping back to the photo
  // the viewer was opened on.
  const [viewedPhotoId, setViewedPhotoId] = useState(selectedPhotoId);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | undefined>();

  useEffect(() => {
    setViewedPhotoId(selectedPhotoId);
  }, [selectedPhotoId]);

  const currentIndex = useMemo(() => {
    const viewedIndex = photos.findIndex((photo) => photo.galleryId === viewedPhotoId);
    return viewedIndex === -1 ? 0 : viewedIndex;
  }, [photos, viewedPhotoId]);

  const currentPhoto = useMemo(() => photos[currentIndex] || photos[0], [photos, currentIndex]);
  const prediction = highestScoringPredictionForFile(currentPhoto?.predictionSourceUploadId || "", predictions);
  const boundingBox = prediction ? boxForPrediction(prediction) : undefined;
  const hasBoundingBox = Boolean(boundingBox);
  const photoUrl = currentPhoto ? getPhotoImageUrl(currentPhoto) : "";
  const captionStr = includePredictionsInCaption
    ? `Slide ${currentIndex + 1}. ${descriptionForPrediction(prediction)}`
    : `Slide ${currentIndex + 1} of ${photos.length}`;
  const timestamp = currentPhoto ? formatPhotoNumberOverlayTimestamp(getPhotoCapturedAtMs(currentPhoto)) : "";

  const isFullscreen = variant === "fullscreen";
  const imageClassName = cn("w-full object-contain", isFullscreen ? "max-h-[calc(100dvh-14rem)]" : "max-h-[60vh]");

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) setViewedPhotoId(photos[currentIndex - 1].galleryId);
  }, [currentIndex, photos]);

  const goToNext = useCallback(() => {
    if (currentIndex < photos.length - 1) setViewedPhotoId(photos[currentIndex + 1].galleryId);
  }, [currentIndex, photos]);

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableKeyboardTarget(event.target)) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goToNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, goToPrevious, goToNext]);

  useEffect(() => {
    let isCurrent = true;
    setImageDimensions(undefined);
    const loadImageDimensions = async () => {
      if (!photoUrl || !hasBoundingBox) {
        return;
      }
      try {
        const image = (await ImagesDatabase.getInstance().getImage(photoUrl)) as unknown as HTMLImageElement;
        if (isCurrent) {
          setImageDimensions({ width: image.width, height: image.height });
        }
      } catch {
        if (isCurrent) {
          setImageDimensions(undefined);
        }
      }
    };
    loadImageDimensions();
    return () => {
      isCurrent = false;
    };
  }, [photoUrl, hasBoundingBox]);

  if (!currentPhoto) {
    return null;
  }

  return (
    <div className="px-4">
      <div className="flex w-full justify-end pt-2 pb-4">
        <button type="button" aria-label="Close photo preview" onClick={onClose} className="rounded-sm p-1 hover:bg-gray-100">
          <X className="h-8 w-8" />
        </button>
      </div>
      <div className="relative flex flex-col items-center justify-center">
        <ZoomableImage
          resetKey={currentIndex}
          showControls={true}
          autoZoomToBox={boundingBox}
          imageDimensions={imageDimensions}
          autoZoomScale={2.5}
          className="w-full"
        >
          {boundingBox ? (
            <BoundingBoxImage url={photoUrl} rect={boundingBox} box className={imageClassName} />
          ) : (
            <img src={photoUrl} alt="Photo preview" className={imageClassName} />
          )}
        </ZoomableImage>
        {/* Paging here crosses rooms, and nothing else on screen says which one you're looking at. */}
        <PhotoRoomLabels roomPath={currentPhoto?.roomPath} className="flex justify-center pt-3" />
        <PhotoPreviewLabels
          photo={currentPhoto}
          estimateId={estimateId}
          onAddLabel={onAddLabel}
          onRemoveLabel={onRemoveLabel}
        />
        {timestamp && <div className="mt-2 text-center text-sm text-gray-700">{timestamp}</div>}
        <div className="mt-2 pb-2 text-center text-xs text-gray-900">{captionStr}</div>
        <button
          type="button"
          aria-label="Previous photo"
          onClick={goToPrevious}
          className="absolute top-1/2 left-2 -translate-y-1/2 transform"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ChevronLeft size={48} opacity={currentIndex === 0 ? 0.3 : 0.7} />
        </button>
        <button
          type="button"
          aria-label="Next photo"
          onClick={goToNext}
          className="absolute top-1/2 right-2 -translate-y-1/2 transform"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ChevronRight size={48} opacity={currentIndex === photos.length - 1 ? 0.3 : 0.7} />
        </button>
      </div>
    </div>
  );
}
